import { AssetCategory, ValidationResult, REQUIRED_FIELDS_BY_CATEGORY } from '@/types/storageInventory';

/**
 * Regex pattern for validating YYYY-MM-DD date format.
 * Validates structure only (4-digit year, 2-digit month 01-12, 2-digit day 01-31).
 */
const DATE_FORMAT_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Fields that should be validated as dates when provided */
const DATE_FIELDS = ['ship_date', 'support_end_date'];

/** Minimum valid quantity for network devices */
const MIN_QUANTITY = 1;

/** Maximum valid quantity for network devices */
const MAX_QUANTITY = 9999;

/**
 * Validates an asset's data based on its category.
 *
 * Checks:
 * - All required fields for the given category are present and non-empty
 * - For network_devices: quantity is an integer in [1, 9999]
 * - For date fields (ship_date, support_end_date): format is YYYY-MM-DD if provided
 *
 * @param category - The asset category to validate against
 * @param data - The asset data to validate
 * @returns ValidationResult with valid flag and field-level error messages
 */
export function validateAsset(
  category: AssetCategory,
  data: Record<string, unknown>
): ValidationResult {
  const errors: Record<string, string> = {};
  const requiredFields = REQUIRED_FIELDS_BY_CATEGORY[category];

  // Validate required fields
  for (const field of requiredFields) {
    const value = data[field];
    if (value === null || value === undefined || value === '') {
      errors[field] = `${field} is required`;
    }
  }

  // Validate quantity for network devices
  if (category === 'network_devices') {
    const quantity = data['quantity'];
    if (quantity !== null && quantity !== undefined && quantity !== '') {
      const numValue = Number(quantity);
      if (!Number.isInteger(numValue) || numValue < MIN_QUANTITY || numValue > MAX_QUANTITY) {
        errors['quantity'] = `quantity must be an integer between ${MIN_QUANTITY} and ${MAX_QUANTITY}`;
      }
    }
  }

  // Validate date fields if provided
  for (const field of DATE_FIELDS) {
    const value = data[field];
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value !== 'string' || !DATE_FORMAT_REGEX.test(value)) {
        errors[field] = `${field} must be in YYYY-MM-DD format`;
      }
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
